import React, { useEffect, useState } from 'react';
import { MessageCircle, Phone, Gift, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface SiteSettings {
  free_chat_credits: number;
  free_voice_minutes: number;
  enable_free_chat: boolean;
  enable_free_voice: boolean;
}

interface PromotionalCredit {
  id: string;
  promo_chat_credits: number | null;
  promo_voice_minutes: number | null;
  start_date: string;
  end_date: string;
  is_active: boolean;
}

export default function TopPromoBanner() {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [activePromo, setActivePromo] = useState<PromotionalCredit | null>(null);
  const [countdown, setCountdown] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCreditsInfo();

    // Subscribe to real-time changes
    const settingsChannel = supabase
      .channel('top_banner_settings')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'site_settings' }, () => {
        fetchCreditsInfo();
      })
      .subscribe();

    const promosChannel = supabase
      .channel('top_banner_promos')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'promotional_credits' }, () => {
        fetchCreditsInfo();
      })
      .subscribe();

    const interval = setInterval(() => {
      fetchCreditsInfo();
    }, 60000);

    return () => {
      clearInterval(interval);
      settingsChannel.unsubscribe();
      promosChannel.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!activePromo) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const end = new Date(activePromo.end_date).getTime();
      const distance = end - now;

      if (distance < 0) {
        setCountdown('Expired');
        fetchCreditsInfo();
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      if (days > 0) {
        setCountdown(`${days}d ${hours}h`);
      } else if (hours > 0) {
        setCountdown(`${hours}h ${minutes}m`);
      } else if (minutes > 0) {
        setCountdown(`${minutes}m ${seconds}s`);
      } else {
        setCountdown(`${seconds}s`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [activePromo]);

  const fetchCreditsInfo = async () => {
    try {
      const [settingsResult, promosResult] = await Promise.all([
        supabase.from('site_settings').select('*').eq('id', 1).single(),
        supabase
          .from('promotional_credits')
          .select('*')
          .eq('is_active', true)
          .gte('end_date', new Date().toISOString())
          .lte('start_date', new Date().toISOString())
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()
      ]);

      if (settingsResult.data) {
        setSettings(settingsResult.data);
      }

      if (promosResult.data) {
        setActivePromo(promosResult.data);
      } else {
        setActivePromo(null);
      }
    } catch (err) {
      console.error('Error fetching credits info:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !settings) {
    return null;
  }

  const chatCredits = activePromo?.promo_chat_credits ?? (settings.enable_free_chat ? settings.free_chat_credits : 0);
  const voiceMinutes = activePromo?.promo_voice_minutes ?? (settings.enable_free_voice ? settings.free_voice_minutes : 0);
  const hasChat = chatCredits > 0;
  const hasVoice = voiceMinutes > 0;

  // Don't show banner if no credits are available
  if (!hasChat && !hasVoice) {
    return null;
  }

  // Build the message text
  let messageText = '';
  if (hasChat && hasVoice) {
    messageText = `Get ${chatCredits} FREE ${chatCredits === 1 ? 'Message' : 'Messages'} + ${voiceMinutes} FREE Voice Call ${voiceMinutes === 1 ? 'Minute' : 'Minutes'}!`;
  } else if (hasChat) {
    messageText = `Get ${chatCredits} FREE ${chatCredits === 1 ? 'Message' : 'Messages'}!`;
  } else {
    messageText = `Get ${voiceMinutes} FREE Voice Call ${voiceMinutes === 1 ? 'Minute' : 'Minutes'}!`;
  }

  if (activePromo) {
    return (
      <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white">
        <div className="container mx-auto px-4 py-4 text-center">
          <p className="text-xl font-bold flex items-center justify-center gap-2 flex-wrap">
            <Gift className="w-6 h-6 inline animate-bounce" />
            <span>New Users: {messageText} Limited Time Offer</span>
            <Clock className="w-5 h-5 inline ml-2" />
            <span className="text-lg">Ends in {countdown}</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white">
      <div className="container mx-auto px-4 py-4 text-center">
        <p className="text-xl font-bold">
          <Gift className="w-6 h-6 inline mr-2" />
          New Users: {messageText}
        </p>
      </div>
    </div>
  );
}
